class ModernHeroSlider extends HTMLElement {
  connectedCallback() {
    if (this.hasAttribute('data-slider-initialized')) return;
    this.setAttribute('data-slider-initialized', 'true');

    this.slides = Array.from(this.querySelectorAll('[data-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-dot]'));
    this.previousButton = this.querySelector('[data-previous]');
    this.nextButton = this.querySelector('[data-next]');
    this.currentOutput = this.querySelector('[data-current-slide]');
    this.progressFill = this.querySelector('[data-progress-fill]');

    const initiallyActiveIndex = Math.max(
      0,
      this.slides.findIndex((slide) => slide.classList.contains('is-active'))
    );

    this.currentIndex = -1;
    this.transitionId = 0;
    this.transitionTimers = new Set();

    this.autoplayEnabled =
      this.dataset.autoplay === 'true' && this.slides.length > 1;
    this.designMode = this.dataset.designMode === 'true';
    this.autoplayInEditor = this.dataset.autoplayInEditor !== 'false';
    this.pauseOnHover =
      this.dataset.pauseOnHover === 'true' && !this.designMode;

    this.duration = Math.max(
      1000,
      Number.parseInt(this.dataset.autoplaySpeed || '6', 10) * 1000
    );

    this.fadeDuration = Math.max(
      0,
      Number.parseInt(this.dataset.fadeDuration || '900', 10)
    );

    this.timer = undefined;
    this.progressAnimation = undefined;
    this.pauseReasons = new Set();
    this.touchStartX = 0;
    this.touchStartY = 0;

    this.onPreviousClick = () => this.showSlide(this.currentIndex - 1);
    this.onNextClick = () => this.showSlide(this.currentIndex + 1);
    this.onPointerEnter = () => this.pause('hover');
    this.onPointerLeave = () => this.resume('hover');

    this.onVisibilityChange = () => {
      if (document.hidden) {
        this.pause('hidden');
      } else {
        this.resume('hidden');
      }
    };

    this.onTouchStart = (event) => {
      const touch = event.touches[0];
      if (!touch) return;

      this.touchStartX = touch.clientX;
      this.touchStartY = touch.clientY;
    };

    this.onTouchEnd = (event) => {
      const touch = event.changedTouches[0];
      if (!touch) return;

      const deltaX = touch.clientX - this.touchStartX;
      const deltaY = touch.clientY - this.touchStartY;

      if (Math.abs(deltaX) < 45 || Math.abs(deltaX) <= Math.abs(deltaY)) {
        return;
      }

      this.showSlide(
        deltaX > 0 ? this.currentIndex - 1 : this.currentIndex + 1
      );
    };

    this.onBlockSelect = (event) => {
      const selectedSlide = event.target.closest?.('[data-slide]');
      if (!selectedSlide || !this.contains(selectedSlide)) return;

      const selectedIndex = this.slides.indexOf(selectedSlide);
      if (selectedIndex < 0) return;

      this.showSlide(selectedIndex);

      if (this.designMode && !this.autoplayInEditor) {
        this.pause('theme-editor');
      }
    };

    this.onBlockDeselect = () => this.resume('theme-editor');

    this.previousButton?.addEventListener('click', this.onPreviousClick);
    this.nextButton?.addEventListener('click', this.onNextClick);

    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const index = Number.parseInt(dot.dataset.dot || '0', 10);
        this.showSlide(index);
      });
    });

    if (this.pauseOnHover) {
      this.addEventListener('pointerenter', this.onPointerEnter);
      this.addEventListener('pointerleave', this.onPointerLeave);
    }

    this.addEventListener('touchstart', this.onTouchStart, { passive: true });
    this.addEventListener('touchend', this.onTouchEnd, { passive: true });

    document.addEventListener('visibilitychange', this.onVisibilityChange);
    document.addEventListener('shopify:block:select', this.onBlockSelect);
    document.addEventListener('shopify:block:deselect', this.onBlockDeselect);

    // Reset the server-rendered active state so the initial text and image
    // animation also starts reliably after the JavaScript connects.
    this.slides.forEach((slide) => {
      slide.classList.remove('is-active', 'is-leaving');
      slide.setAttribute('aria-hidden', 'true');
      slide.inert = true;
    });

    this.showSlide(initiallyActiveIndex);
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
    this.progressAnimation?.cancel();

    this.transitionTimers.forEach((timer) => clearTimeout(timer));
    this.transitionTimers.clear();

    this.previousButton?.removeEventListener('click', this.onPreviousClick);
    this.nextButton?.removeEventListener('click', this.onNextClick);

    if (this.pauseOnHover) {
      this.removeEventListener('pointerenter', this.onPointerEnter);
      this.removeEventListener('pointerleave', this.onPointerLeave);
    }

    this.removeEventListener('touchstart', this.onTouchStart);
    this.removeEventListener('touchend', this.onTouchEnd);

    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
    document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
  }

  normalizeIndex(index) {
    if (this.slides.length === 0) return 0;

    return (
      ((index % this.slides.length) + this.slides.length) %
      this.slides.length
    );
  }

  showSlide(index) {
    if (this.slides.length === 0) return;

    const nextIndex = this.normalizeIndex(index);
    const incomingSlide = this.slides[nextIndex];
    const outgoingSlide =
      this.currentIndex >= 0 ? this.slides[this.currentIndex] : undefined;

    const transitionId = ++this.transitionId;

    this.slides.forEach((slide) => {
      if (slide !== outgoingSlide && slide !== incomingSlide) {
        slide.classList.remove('is-active', 'is-leaving');
        slide.setAttribute('aria-hidden', 'true');
        slide.inert = true;
      }
    });

    if (outgoingSlide && outgoingSlide !== incomingSlide) {
      outgoingSlide.classList.remove('is-active');
      outgoingSlide.classList.add('is-leaving');
      outgoingSlide.setAttribute('aria-hidden', 'true');
      outgoingSlide.inert = true;

      const cleanupTimer = window.setTimeout(() => {
        this.transitionTimers.delete(cleanupTimer);

        if (transitionId <= this.transitionId) {
          outgoingSlide.classList.remove('is-leaving');
        }
      }, this.fadeDuration + 80);

      this.transitionTimers.add(cleanupTimer);
    }

    incomingSlide.classList.remove('is-active', 'is-leaving');
    incomingSlide.setAttribute('aria-hidden', 'false');
    incomingSlide.inert = false;

    // Force the browser to commit the hidden starting state. Adding
    // is-active on the next frame then produces a genuine opacity transition
    // and restarts the content and image keyframe animations.
    void incomingSlide.offsetWidth;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (transitionId !== this.transitionId) return;
        incomingSlide.classList.add('is-active');
      });
    });

    this.currentIndex = nextIndex;

    this.dots.forEach((dot, dotIndex) => {
      const active = dotIndex === this.currentIndex;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-selected', active ? 'true' : 'false');
    });

    if (this.currentOutput) {
      this.currentOutput.textContent = String(this.currentIndex + 1).padStart(
        2,
        '0'
      );
    }

    this.restartAutoplay();
  }

  restartAutoplay() {
    clearTimeout(this.timer);
    this.timer = undefined;
    this.restartProgress();

    if (!this.canAutoplay()) return;

    this.timer = window.setTimeout(() => {
      this.showSlide(this.currentIndex + 1);
    }, this.duration);
  }

  canAutoplay() {
    return this.autoplayEnabled && this.pauseReasons.size === 0;
  }

  pause(reason) {
    this.pauseReasons.add(reason);
    this.classList.add('is-paused');

    clearTimeout(this.timer);
    this.timer = undefined;
    this.progressAnimation?.pause();
  }

  resume(reason) {
    this.pauseReasons.delete(reason);

    if (this.pauseReasons.size === 0) {
      this.classList.remove('is-paused');
    }

    if (!this.canAutoplay()) return;
    this.restartAutoplay();
  }

  restartProgress() {
    if (!this.progressFill) return;

    this.progressAnimation?.cancel();
    this.progressFill.style.transform = 'scaleX(0)';

    if (!this.autoplayEnabled) return;

    this.progressAnimation = this.progressFill.animate(
      [
        { transform: 'scaleX(0)' },
        { transform: 'scaleX(1)' }
      ],
      {
        duration: this.duration,
        easing: 'linear',
        fill: 'forwards'
      }
    );

    if (!this.canAutoplay()) {
      this.progressAnimation.pause();
    }
  }
}

if (!customElements.get('modern-hero-slider')) {
  customElements.define('modern-hero-slider', ModernHeroSlider);
}